import React, { useEffect, useRef, useState } from "react";
import "./MotionHomepage.css";
import { motionMedia } from "./motionMedia";

function LazyVideo({
  slot,
  className,
  poster,
  mp4,
  webm,
  priority = false,
  labelledBy,
  onPlaying,
  onTimeUpdate,
}) {
  const videoRef = useRef(null);
  const isTestDom =
    typeof window !== "undefined" &&
    window.navigator.userAgent.toLowerCase().includes("jsdom");

  useEffect(() => {
    const video = videoRef.current;
    if (!video || typeof window === "undefined") {
      return undefined;
    }
    if (window.navigator.userAgent.toLowerCase().includes("jsdom")) {
      return undefined;
    }

    const requestPlay = () => {
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(() => {});
      }
    };

    const hydrateVideo = () => {
      if (video.dataset.loaded === "true") {
        requestPlay();
        return;
      }
      Array.from(video.querySelectorAll("source")).forEach((source) => {
        if (source.dataset.src) {
          source.src = source.dataset.src;
        }
      });
      video.dataset.loaded = "true";
      video.load();
      requestPlay();
    };

    if (!("IntersectionObserver" in window)) {
      hydrateVideo();
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          hydrateVideo();
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={videoRef}
      className={className}
      data-media-slot={slot}
      poster={poster}
      data-loaded={priority ? "true" : undefined}
      preload={priority ? "auto" : "none"}
      muted
      playsInline
      loop
      autoPlay={!isTestDom}
      aria-labelledby={labelledBy}
      onCanPlay={(event) => {
        const playAttempt = event.currentTarget.play();
        if (playAttempt && typeof playAttempt.catch === "function") {
          playAttempt.catch(() => {});
        }
      }}
      onPlaying={onPlaying}
      onTimeUpdate={onTimeUpdate}
    >
      {webm && <source data-src={webm} src={priority ? webm : undefined} type="video/webm" />}
      {mp4 && <source data-src={mp4} src={priority ? mp4 : undefined} type="video/mp4" />}
    </video>
  );
}

function ScrollScrub({ children }) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof window === "undefined") {
      return undefined;
    }

    let frame = 0;
    const smoothStep = (value, start, end) => {
      const normalized = Math.min(1, Math.max(0, (value - start) / (end - start)));
      return normalized * normalized * (3 - 2 * normalized);
    };
    const update = () => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const range = Math.max(1, rect.height - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / range));
      const introProgress = smoothStep(progress, 0.1, 0.2);
      const scaleProgress = smoothStep(progress, 0.24, 0.78);
      const whiteProgress = smoothStep(progress, 0.76, 0.88);
      const copyProgress = smoothStep(progress, 0.86, 0.96);
      const copyStepOneProgress = smoothStep(progress, 0.87, 0.91);
      const copyStepTwoProgress = smoothStep(progress, 0.9, 0.94);
      const copyStepThreeProgress = smoothStep(progress, 0.93, 0.97);
      const releaseProgress = smoothStep(progress, 0.985, 1);
      element.style.setProperty("--scroll-progress", progress.toFixed(4));
      element.style.setProperty("--wordmark-intro-progress", introProgress.toFixed(4));
      element.style.setProperty("--wordmark-scale-progress", scaleProgress.toFixed(4));
      element.style.setProperty("--wordmark-white-progress", whiteProgress.toFixed(4));
      element.style.setProperty("--wordmark-copy-progress", copyProgress.toFixed(4));
      element.style.setProperty("--wordmark-copy-step-1", copyStepOneProgress.toFixed(4));
      element.style.setProperty("--wordmark-copy-step-2", copyStepTwoProgress.toFixed(4));
      element.style.setProperty("--wordmark-copy-step-3", copyStepThreeProgress.toFixed(4));
      element.style.setProperty("--wordmark-release-progress", releaseProgress.toFixed(4));
    };
    const requestUpdate = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  return (
    <section ref={ref} className="motion-scrub-section">
      {children}
    </section>
  );
}

export default function MotionHomepage() {
  const [activeManifesto, setActiveManifesto] = useState(0);
  const [activeStory, setActiveStory] = useState(0);
  const [heroHasPlayed, setHeroHasPlayed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return undefined;
    }

    const nodes = Array.from(document.querySelectorAll("[data-motion-step]"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveManifesto(Number(entry.target.getAttribute("data-motion-step")));
          }
        });
      },
      { threshold: 0.55 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      return undefined;
    }

    const nodes = Array.from(document.querySelectorAll("[data-motion-story-step]"));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveStory(Number(entry.target.getAttribute("data-motion-story-step")));
          }
        });
      },
      { threshold: 0.62 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const active = motionMedia.manifesto[activeManifesto] || motionMedia.manifesto[0];
  const activeSystemStory =
    motionMedia.systemStory.steps[activeStory] || motionMedia.systemStory.steps[0];

  return (
    <div className="motion-homepage">
      <section className="motion-hero" aria-labelledby="motion-hero-title">
        <LazyVideo
          slot="hero-video"
          className="motion-hero-video"
          poster={motionMedia.hero.poster}
          mp4={motionMedia.hero.videoMp4}
          webm={motionMedia.hero.videoWebm}
          priority
          labelledBy="motion-hero-title"
          onPlaying={() => setHeroHasPlayed(true)}
        />
        <img
          className="motion-hero-poster"
          data-media-slot="hero-poster"
          src={motionMedia.hero.poster}
          alt=""
        />
        <div className="motion-hero-shade" />
        <div className={`motion-hero-copy${heroHasPlayed ? " is-fading-out" : ""}`}>
          <p className="motion-eyebrow">ZENTO</p>
          <h1 id="motion-hero-title">생산성에 집착하는 AI Native Team</h1>
          <p>
            제품, 비즈니스, AI 자동화를 하나의 실행력으로 연결합니다.
          </p>
        </div>
      </section>

      <ScrollScrub>
        <div className="motion-video-synced-headline" aria-hidden="true">
          <LazyVideo
            slot="hero-wordmark-full-video"
            className="motion-video-synced-full-video"
            poster={motionMedia.hero.poster}
            mp4={motionMedia.hero.videoMp4}
            webm={motionMedia.hero.videoWebm}
          />
          <LazyVideo
            slot="hero-wordmark-video"
            className="motion-video-synced-mask-video"
            poster={motionMedia.hero.poster}
            mp4={motionMedia.hero.videoMp4}
            webm={motionMedia.hero.videoWebm}
          />
          <div className="motion-video-synced-contrast" />
          <div className="motion-video-synced-white" />
          <span className="motion-video-synced-label">ZENTO</span>
          <div className="motion-video-synced-final-copy">
            <p>AI는 도구가 아니라 함께 움직이는 팀원입니다.</p>
            <h2>AI와 함께 일합니다.</h2>
            <div className="motion-video-synced-action-plan">
              <span className="motion-video-synced-action motion-video-synced-action-1">
                <b>01</b>
                <strong>흐름을 읽습니다.</strong>
                <em>문제와 반복 업무를 먼저 발견합니다.</em>
              </span>
              <span className="motion-video-synced-action motion-video-synced-action-2">
                <b>02</b>
                <strong>AI를 배치합니다.</strong>
                <em>실행 가능한 자동화 구조로 전환합니다.</em>
              </span>
              <span className="motion-video-synced-action motion-video-synced-action-3">
                <b>03</b>
                <strong>성과로 개선합니다.</strong>
                <em>데이터를 보고 다음 액션을 정합니다.</em>
              </span>
            </div>
          </div>
        </div>
      </ScrollScrub>

      <section className="motion-pinned-manifesto" aria-label="ZENTO Manifesto motion">
        <div className="motion-manifesto-stage">
          <div className="motion-manifesto-media">
            <img
              className="motion-manifesto-poster"
              src={active.poster}
              alt=""
              aria-hidden="true"
            />
            {motionMedia.manifesto.map((item, index) => (
              <div
                className={`motion-manifesto-layer${index === activeManifesto ? " is-active" : ""}`}
                key={item.key}
              >
                <LazyVideo
                  slot={`${item.key}-video`}
                  className="motion-manifesto-video"
                  poster={item.poster}
                  mp4={item.video}
                  webm={item.videoWebm}
                  labelledBy={`motion-step-title-${item.key}`}
                />
              </div>
            ))}
          </div>
          <div className="motion-manifesto-copy">
            <p className="motion-eyebrow">ZENTO Manifesto</p>
            <p className="motion-manifesto-label">{active.label}</p>
            <h2 id={`motion-step-title-${active.key}`}>{active.title}</h2>
            <h3>{active.kicker}</h3>
            <p>{active.body}</p>
          </div>
        </div>
        <div className="motion-manifesto-steps">
          {motionMedia.manifesto.map((item, index) => (
            <article
              aria-hidden="true"
              className="motion-manifesto-step"
              data-motion-step={index}
              key={item.key}
            >
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{item.label}</h3>
              <p>{item.kicker}</p>
              <strong className="motion-step-title">{item.title}</strong>
            </article>
          ))}
        </div>
      </section>

      <section className="motion-pinned-video-story" aria-labelledby="motion-story-title">
        <div className="motion-story-stage">
          <div className="motion-story-media">
            <LazyVideo
              slot="system-video"
              className="motion-system-video"
              poster={motionMedia.systemStory.poster}
              mp4={motionMedia.systemStory.videoMp4}
              webm={motionMedia.systemStory.videoWebm}
              labelledBy="motion-story-title"
            />
            <div className="motion-story-media-overlay" />
          </div>
          <div className="motion-story-copy" aria-live="polite">
            <p className="motion-eyebrow">AI 실행 흐름</p>
            <h2 id="motion-story-title">문제 발견부터 개선까지, 하나의 운영 리듬으로.</h2>
            <div className="motion-story-current">
              <span>{activeSystemStory.label}</span>
              <h3>{activeSystemStory.title}</h3>
              <p>{activeSystemStory.body}</p>
            </div>
          </div>
        </div>
        <div className="motion-story-steps">
          {motionMedia.systemStory.steps.map((item, index) => (
            <article
              aria-hidden="true"
              className="motion-story-step"
              data-motion-story-step={index}
              key={item.label}
            >
              <span>{item.label}</span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="motion-showcase" aria-labelledby="motion-showcase-title">
        <div className="motion-showcase-copy">
          <p className="motion-eyebrow">운영 시스템</p>
          <h2 id="motion-showcase-title">작은 팀이 큰 플랫폼처럼 움직이는 방식.</h2>
          <p>
            자동화, 데이터, AI 에이전트, 제품 실행을 하나의 운영 리듬으로 묶어
            실험과 배포의 속도를 끌어올립니다.
          </p>
          <div className="motion-showcase-pills">
            <span>AI 전략</span>
            <span>자동화</span>
            <span>제품 운영</span>
            <span>성장 데이터</span>
          </div>
        </div>
        <div className="motion-device-frame">
          <LazyVideo
            slot="feature-video"
            className="motion-feature-video"
            poster={motionMedia.showcase.poster}
            mp4={motionMedia.showcase.videoMp4}
            webm={motionMedia.showcase.videoWebm}
            labelledBy="motion-showcase-title"
          />
          <img
            className="motion-gallery-image"
            data-media-slot="gallery-image"
            src={motionMedia.showcase.image}
            alt="ZENTO 실행 시스템 이미지"
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}
